import { HttpClient, HttpContext, httpResource, HttpResourceRef } from "@angular/common/http";
import { inject, Injectable, Signal } from "@angular/core";
import { CACHE_CONFIG } from "@kno2/core/http";
import { SessionService } from "@kno2/core/session";
import { hoursToMilliseconds } from "date-fns";
import { firstValueFrom } from "rxjs";

export type RuleType = "Intake" | "Triage";

export interface Rule {
    id: string;
    name: string;
    fromAddress?: string;
    toAddress?: string;
    documentType?: string;
    sourceType?: string;
    ruleType: RuleType;
    ruleAssignments: string[];
    checked: boolean;
    isAssociatedWithAllActiveOrganizationUsers: boolean;
}

@Injectable({
    providedIn: "root"
})
export class RulesService {
    private readonly httpClient = inject(HttpClient);
    private readonly sessionService = inject(SessionService);

    public getRule(id: Signal<string>): HttpResourceRef<Rule> {
        return httpResource(() => ({
            url: `/api/rules/${id()}`,
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true, maxAge: hoursToMilliseconds(1) })
        }));
    }

    public getRulesByType(type: Signal<RuleType>): HttpResourceRef<Rule[]> {
        return httpResource(() => ({
            url: `/api/rules`,
            params: { type: type() },
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true, maxAge: hoursToMilliseconds(1) })
        }));
    }

    public getRules(type?: Signal<RuleType>): HttpResourceRef<Rule[]> {
        return httpResource(() => ({
            url: `/api/rules`,
            params: type ? { type: type() } : undefined,
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true, maxAge: hoursToMilliseconds(1) })
        }));
    }

    public getRulesAsync(): Promise<Rule[]> {
        return firstValueFrom(
            this.httpClient.get<Rule[]>(`/api/rules`, {
                context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true, maxAge: hoursToMilliseconds(1) })
            })
        );
    }

    public upsertRule(rule: Partial<Rule>): Promise<Rule> {
        const method = rule.id ? "PUT" : "POST";

        return firstValueFrom(
            this.httpClient.request<Rule>(method, `/api/rules/${rule.id || ""}`, {
                body: rule,
                context: new HttpContext().set(CACHE_CONFIG, { invalidateCache: ["/api/rules", `/api/users/${this.userId}/rules`] })
            })
        );
    }

    public deleteRule(id: string): Promise<void> {
        return firstValueFrom(
            this.httpClient.delete<void>(`/api/rules/${id}`, {
                context: new HttpContext().set(CACHE_CONFIG, { invalidateCache: ["/api/rules", `/api/users/${this.userId}/rules`] })
            })
        );
    }

    private get userId(): string {
        return this.sessionService.profileData()!.userId;
    }
}
